import dynamic from "next/dynamic";

import React, { useMemo } from "react";

import { parseJSON } from "date-fns";
import numeral from "numeral";

import { Loader } from "components/customElements/loader";
import { colors } from "consts";

import ToggleTimeScope, { useTimeScope } from "./ToggleTimeScope";
import useFinancialData from "./useFinancialData";

const TimeSeriesChart = dynamic(
  () => import("components/AmChart/TimeSeriesChart"),
  {
    ssr: false,
  }
);

const ESChartConfig = {
  title: "EV/Sales",
  charts: [
    {
      type: "column",
      name: "EV/Sales",
      fieldName: "ev_sales",
      color: colors.smartTeal,
    },
  ],
};

const EGChartConfig = {
  title: "EV/Gross Profit",
  charts: [
    {
      type: "column",
      name: "EV/Gross Profit",
      fieldName: "ev_gp",
      color: colors.smartTeal,
    },
  ],
};

const EEChartConfig = {
  title: "EV/EBITDA",
  charts: [
    {
      type: "column",
      name: "EV/EBITDA",
      fieldName: "ev_ebitda",
      color: colors.smartTeal,
      tooltipFormat: "{name}: {ev_ebitda_tooltip}",
    },
  ],
};

const PEChartConfig = {
  title: "P/E",
  charts: [
    {
      type: "column",
      name: "PE",
      fieldName: "pe",
      color: colors.smartTeal,
    },
  ],
};

const PBChartConfig = {
  title: "P/B",
  charts: [
    {
      type: "column",
      name: "PB",
      fieldName: "pb",
      color: colors.smartTeal,
    },
  ],
};

interface Props {
  entity: any;
}

const Valuation = ({ entity }: Props) => {
  const { isAnnual } = useTimeScope();
  const {
    timeScopeData: chartData,
    currentStats,
    isLoading,
  } = useFinancialData(entity?.isin || "");

  const data = useMemo(() => {
    const data = chartData.map((item) => {
      const isLast =
        parseJSON(item.date).getFullYear() === new Date().getFullYear();

      let ev_ebitda = (isLast ? currentStats?.ev_ebitda : item.ev_ebitda) || 0;
      if (ev_ebitda < 0) {
        return {
          ...item,
          ev_ebitda: 0,
          ev_ebitda_tooltip: "Not meaningful",
        };
      }

      return isAnnual
        ? {
            ...item,
            columnSettings: isLast ? { fill: colors.grass } : {},
            categoryLabel: isLast ? "Latest" : "",
            ev_ebitda,
            ev_ebitda_tooltip: numeral(ev_ebitda).format("0.00"),
            ev_gp: isLast ? currentStats?.ev_gp : item.ev_gp,
            ev_sales: isLast ? currentStats?.ev_sales : item.ev_sales,
            pe: isLast ? currentStats?.pe : item.pe,
            pb: isLast ? currentStats?.pb : item.pb,
          }
        : {
            ...item,
            ev_ebitda,
            ev_ebitda_tooltip: numeral(ev_ebitda).format("0.00"),
          };
    });
    if (
      isAnnual &&
      parseJSON(data[data.length - 1]?.date).getFullYear() !==
        new Date().getFullYear()
    ) {
      const ev_ebitda =
        currentStats?.ev_ebitda && currentStats?.ev_ebitda > 0
          ? currentStats?.ev_ebitda
          : 0;
      data.push({
        ...data[data.length - 1], // temporary clone all other properties from last year
        date: currentStats?.mkt_val_date || "",
        columnSettings: { fill: colors.grass },
        categoryLabel: "Latest",
        ev_ebitda,
        ev_ebitda_tooltip:
          !currentStats?.ev_ebitda || currentStats?.ev_ebitda < 0
            ? "Not meaningful"
            : numeral(ev_ebitda).format("0.00"),
        ev_gp: currentStats?.ev_gp,
        ev_sales: currentStats?.ev_sales,
        pe: currentStats?.pe,
        pb: currentStats?.pb,
      });
    }

    return data;
  }, [
    chartData,
    currentStats?.ev_ebitda,
    currentStats?.ev_gp,
    currentStats?.ev_sales,
    currentStats?.mkt_val_date,
    currentStats?.pb,
    currentStats?.pe,
    isAnnual,
  ]);

  const hasESData = useMemo(
    () => data?.some((item) => !!item.ev_sales),
    [data]
  );
  const hasEGData = useMemo(() => data?.some((item) => !!item.ev_gp), [data]);
  const hasEEData = useMemo(
    () => data?.some((item) => !!item.ev_ebitda),
    [data]
  );
  const hasPEData = useMemo(() => data?.some((item) => !!item.pe), [data]);
  const hasPBData = useMemo(() => data?.some((item) => !!item.pb), [data]);

  if (isLoading) return <Loader />;

  const chartClass = "w-full h-[400px] mb-[80px]";
  return (
    <>
      <ToggleTimeScope />
      {hasESData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={data}
            chartConfig={ESChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasEGData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={data}
            chartConfig={EGChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasEEData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={data}
            chartConfig={EEChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasPEData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={data}
            chartConfig={PEChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasPBData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={data}
            chartConfig={PBChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
    </>
  );
};

export default Valuation;
